import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import type { Quote, QuoteListParams } from "./types";

interface ListMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

interface QuoteListBody {
  data?: Quote[];
  meta?: ListMeta;
}

interface QuoteDetailBody {
  data?: { data?: Quote };
}

export interface QuoteListResult {
  data: Quote[];
  meta?: ListMeta;
}

function cleanParams(params: QuoteListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {
    page: params.page ?? 1,
    limit: params.limit ?? 20,
  };
  if (params.status) out.status = params.status;
  if (params.crm_company_id) out.crm_company_id = params.crm_company_id;
  if (params.trip_id) out.trip_id = params.trip_id;
  if (params.route_id) out.route_id = params.route_id;
  if (params.date_from) out.date_from = params.date_from;
  if (params.date_to) out.date_to = params.date_to;
  if (params.search) out.search = params.search;
  return out;
}

export function useQuotesList(params: QuoteListParams = {}, options: { enabled?: boolean } = {}) {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.quotes.list(companyId, params),
    queryFn: ({ signal }) =>
      getJson<QuoteListBody>("/quotesList", cleanParams(params), { signal }).then((body) => ({
        data: body.data ?? [],
        meta: body.meta,
      })),
    enabled: (options.enabled ?? true) && !!companyId,
  });
}

export function useQuote(quoteId: string | null | undefined, options: { enabled?: boolean } = {}) {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.quotes.detail(companyId, quoteId ?? null),
    queryFn: ({ signal }) =>
      getJson<QuoteDetailBody>("/quotes", { id: quoteId }, { signal }).then((body) => body.data?.data ?? null),
    enabled: (options.enabled ?? true) && !!quoteId && !!companyId,
  });
}
